import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { ContentSkeleton } from 'src/components/ContentSkeleton';
import { useDeferredLoading } from 'src/hooks/useDeferredLoading';
import { useAdminPermissions } from 'src/hooks/useAdminPermissions';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_USERS } from 'src/utils/routes';
import { convertDate } from 'src/components/Table/helpers';
import { AdminPlatformUser, fetchPlatformUser, formatKycStatusLabel } from 'src/api/adminUsers';
import {
  KycRecord,
  fetchKycRecord,
  formatKycName,
  formatProviderLabel,
  getKycUserId,
  kycErrorMessage,
} from 'src/api/adminKyc';

import KycRecordBody from './KycRecordBody';
import KycReviewActions from './KycReviewActions';
import {
  Breadcrumb,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
} from './styled';

type KycDetailsModalProps = {
  kycId: string;
  onClose: () => void;
  onUpdated?: () => void;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const { date, time } = convertDate(String(value));
  return `${date} - ${time}`;
};

const KycDetailsModal: React.FC<KycDetailsModalProps> = ({ kycId, onClose, onUpdated }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = useAdminPermissions();
  const canOpenUser = can(PERMISSIONS.USER_READ);

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<KycRecord | null>(null);
  const [owner, setOwner] = useState<AdminPlatformUser | null>(null);

  // Held in a ref so the record is fetched once per id. Callers pass inline
  // arrows, and an alert re-renders the whole route tree, so depending on
  // `onClose` directly would refire this load on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRecord(await fetchKycRecord(kycId));
    } catch (e: any) {
      dispatch(
        setAppAlert({ message: kycErrorMessage(e, 'Failed to load KYC record'), isSuccess: false }),
      );
      onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [kycId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // The detail response carries only the user's ObjectId. The profile fills in
  // email and BxLend ID, and is skipped for roles without admin:user:read —
  // kyc-management is one of them, so a refusal there is expected, not an error.
  const ownerId = getKycUserId(record);
  useEffect(() => {
    if (!ownerId || !canOpenUser) {
      setOwner(null);
      return undefined;
    }

    let active = true;
    fetchPlatformUser(ownerId)
      .then((user) => {
        if (active) setOwner(user);
      })
      .catch(() => {
        if (active) setOwner(null);
      });

    return () => {
      active = false;
    };
  }, [ownerId, canOpenUser]);

  const showSkeleton = useDeferredLoading(loading || !record, Boolean(record));
  const status = String(record?.status ?? '').toUpperCase();
  const displayName = formatKycName(record?.name) || '—';

  const handleUpdated = async (updated?: KycRecord) => {
    if (updated) {
      setRecord(updated);
    } else {
      await load();
    }
    onUpdated?.();
  };

  return (
    <Modal isOpen onClose={onClose}>
      {showSkeleton || !record ? (
        <ContentSkeleton variant="detailModal" count={4} />
      ) : (
        <>
          <Breadcrumb>KYC / {displayName}</Breadcrumb>
          <HeadlineRow>
            <TitleGroup>
              <Title>
                {displayName}
                <StatusBadge $status={status}>{formatKycStatusLabel(status)}</StatusBadge>
                <StatusBadge $status={String(record.provider ?? '')}>
                  {formatProviderLabel(record.provider)}
                </StatusBadge>
                {record.requires_manual_review ? (
                  <StatusBadge $status="needs review">Needs review</StatusBadge>
                ) : null}
              </Title>
              <MetaLine>
                Created {formatDateTime(record.created_at)} · Updated{' '}
                {formatDateTime(record.updated_at)}
              </MetaLine>
            </TitleGroup>
          </HeadlineRow>

          <KycRecordBody
            record={record}
            owner={owner}
            onOpenUser={
              ownerId && canOpenUser
                ? () => {
                    onClose();
                    navigate(`${ROUTE_USERS}/${ownerId}`);
                  }
                : undefined
            }
          />

          <KycReviewActions record={record} onUpdated={handleUpdated} />
        </>
      )}
    </Modal>
  );
};

export default KycDetailsModal;
